<template>
  <div class="booking-form">
    <div v-show="ready">
      <booking-traveler
        v-for="(traveller, key) in bookingForm.travellers"
        :number="travellerNumber[key]"
        :key="key"
        :editMode="editMode"
        :value="bookingForm.travellers[key]"
        @input="(val) => onUpdateTraveller(val, key)"
        @valid="(valid) => $set(isValid.travelers, key, valid)"
        @edit="gotoEditMode"
      ></booking-traveler>

      <booking-order-details
        :comment="userComment"
        :newsletter="bookingForm.subscribeCustomerToNewsletter"
        :editMode="editMode"
        :value="bookingForm.customer"
        @input="onUpdateOrderDetails"
        @comment="(comment) => userComment = comment"
        @newsletter="
          (newlsetterSignup) =>
            $set(bookingForm, 'subscribeCustomerToNewsletter', newlsetterSignup)
        "
        @valid="(valid) => $set(isValid, 'orderDetails', valid)"
        @validOfferCode="(validCode) => validOfferCode = validCode"
        @edit="gotoEditMode"
      ></booking-order-details>

      <booking-payment-details
        :editMode="editMode"
        :changes="changeList"
        v-model="bookingForm.terms"
        :acceptChanges="customerAcceptsChanges"
        @acceptChanges="(val) => (customerAcceptsChanges = val)"
        @userConsentedPrice="() => $emit('userConsentedPrice')"
        @valid="(valid) => (isValid.bookingPaymentDetails = valid)"
        @edit="gotoEditMode"
      ></booking-payment-details>

      <button
        v-if="editMode || loadingPayment"
        class="btn btn--positive pay-travel-button"
        :class="{ 'item-loading': loadingPayment }"
        v-text="translations.pay_travel_button"
        :disabled="!fieldsAreValid || loadingPayment"
        @click="gotoPayment"
      ></button>

      <booking-payment
        v-if="!editMode"
        :customer="bookingForm.customer"
        @paymenttokens="
          (paymentTokens) => (bookingForm.paymentTokens = paymentTokens)
        "
        @booktravel="doBooking"
        @ready="paymentReady = true"
      ></booking-payment>

      <a class="rejsegarantiordning-btn" href="https://www.skovrejser.dk/rejsegarantiordning" target="_blank">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 521.39 521.39" class="rejsegarantiordning-btn__icon"><path d="m260.69,346.03c12.22,0,29.41-33.05,31.57-85.33h-63.15c2.16,52.29,19.35,85.33,31.57,85.33Z"/><path d="m165.77,239.36h41.81c1.18-29.06,7.18-57.95,17.6-78.34-32.17,12.88-55.39,42.68-59.41,78.34Z"/><path d="m296.21,161.02c10.42,20.39,16.42,49.29,17.6,78.34h41.81c-4.01-35.66-27.24-65.46-59.41-78.34Z"/><path d="m260.69,154.03c-12.22,0-29.41,33.05-31.57,85.33h63.15c-2.16-52.29-19.35-85.33-31.57-85.33Z"/><path d="m165.77,260.69c4.01,35.66,27.24,65.46,59.41,78.34-10.42-20.39-16.42-49.29-17.6-78.34h-41.81Z"/><path d="m410.03,68.69h-21.33v-22.07c0-26.64-25.12-47.46-52.2-40.62l-227.57,62.93c-22.38,1.29-40.23,19.72-40.23,42.42v362.67c0,23.53,19.14,42.67,42.67,42.67h298.67c23.53,0,42.67-19.14,42.67-42.67V111.36c0-23.53-19.14-42.67-42.67-42.67Zm-68.05-42.07c12.45-3.18,25.39,6.91,25.39,20v22.07h-177.5l152.12-42.07Zm-27.95,426.07h-117.33c-5.9,0-10.67-4.77-10.67-10.67s4.77-10.67,10.67-10.67h117.33c5.9,0,10.67,4.77,10.67,10.67s-4.77,10.67-10.67,10.67Zm42.67-42.66h-202.67c-5.9,0-10.67-4.77-10.67-10.67s4.77-10.67,10.67-10.67h202.67c5.9,0,10.67,4.77,10.67,10.67s-4.77,10.67-10.67,10.67Zm-96-42.68c-64.7,0-117.33-52.64-117.33-117.33s52.64-117.33,117.33-117.33,117.33,52.63,117.33,117.33-52.64,117.33-117.33,117.33Z" /><path d="m296.21,339.04c32.17-12.88,55.39-42.68,59.41-78.34h-41.81c-1.18,29.06-7.18,57.95-17.6,78.34Z"/></svg>
          <span class="rejsegarantiordning-btn__text">
              <span>Du er beskyttet af</span>
              <span>Rejsegarantiordningen</span>
          </span>
      </a>
    </div>
  </div>
</template>

<script>
import BookingTraveler from "./booking/booking-traveler.vue";
import BookingOrderDetails from "./booking/booking-order-details.vue";
import BookingPaymentDetails from "./booking/booking-payment-details.vue";
import BookingPayment from "./booking/booking-payment.vue";
import MsgBlock from "./msg-block.vue";
import _ from "underscore";

export default {
  props: {
    changeList: {
      type: Array,
      default() {
        return [];
      },
    },
    ready: { type: Boolean, default: false },
  },
  data() {
    return {
      editMode: true,
      paymentReady: false,
      loadingPayment: false,
      translations: APP_CONFIG.TRANSLATION.BOOKING_FORM,
      showErrors: false,
      disableCustomerWrite: false,
      customerAcceptsChanges: false,
      validOfferCode: false,
      userComment: '',
      bookingForm: {
        travellers: [],
        customer: {},
        comment: "",
        paymentTokens: [],
        terms: false,
        subscribeCustomerToNewsletter: true,
      },
      isValid: {
        travelers: [],
        orderDetails: false,
        bookingPaymentDetails: false,
      },
    };
  },
  beforeDestroy() {
    this.$store.commit('savedBookingFormData', _.pick(this.bookingForm, [
      'travellers',
      'customer'
    ]));
  },
  watch: {
    travellerList() {
      this.setupTravellers();
    },
    currentUserDetails() {
      this.setCustomerDetailsFromUser();
    },
    editMode() {
      this.$emit('editmode', this.editMode);
      if (this.editMode) {
        this.$set(this.bookingForm, "paymentTokens", []);
      }
    },
    paymentReady() {
      if (this.paymentReady) {
        this.loadingPayment = false;
      }
    },
    changeList() {
      if(!_.isEmpty(this.changeList) && !this.editMode) {
        this.editMode = true;
      }
    },
    bookingComment() {
      this.$set(this.bookingForm, 'comment', this.bookingComment);
    }
  },
  created() {
    this.setupTravellers();
    this.setCustomerDetailsFromUser();
    this.$nextTick(() => {
      this.updateBookingFormWithSavedData();
    });
  },
  methods: {
    updateBookingFormWithSavedData() {
      let savedBookingData = Object.assign({}, {
        travellers: [],
        customer: {},
      }, this.$store.state.savedBookingFormData);
            
      //Pupolate traveller data from saved data
      let prefix_select = APP_CONFIG.TRANSLATION.BOOKING_TRAVELER.prefix_select;
      let adultTravellers = savedBookingData.travellers.filter(({type}) => Object.keys(prefix_select).indexOf(type) > -1);
      let childTravellers = savedBookingData.travellers.filter(({type}) => type == 'K');
      let travellers = this.travellerList;

      if (travellers && adultTravellers.length == travellers.adults && childTravellers == travellers.children.length) {
        this.$set(this.bookingForm, 'travellers', Object.assign([ ], savedBookingData.travellers.map((traveller) => Object.assign({}, traveller))));
      }

      //Set saved data for customer
      this.$set(this.bookingForm, 'customer', Object.assign({}, this.bookingForm.customer, savedBookingData.customer));
    },
    setupTravellers() {
      if (this.bookingForm.travellers.length) {
        return;
      }

      let travellers = this.travellerList;

      if (!travellers) {
        return;
      }

      let children = travellers.children;
      let adults = travellers.adults;

      let traveller = {
        firstName: "",
        lastName: "",
        dateOfBirth: "",
        nationality: "DK",
        residenceCountryCode: "DK",
        type: "H",
      };

      if (typeof adults !== "undefined") {
        let adultCount = parseInt(adults);
        for (let i = 1; i <= adultCount; i++) {
          this.bookingForm.travellers.push(Object.assign({}, traveller));
        }
      }

      if (typeof children === "object") {
        let childCount = children.length;
        for (let i = 1; i <= childCount; i++) {
          this.bookingForm.travellers.push(
            Object.assign({}, traveller, {
              type: "K",
            })
          );
        }
      }
    },
    onUpdateTraveller(traveller, key) {
      this.$set(this.bookingForm.travellers, key, traveller);

      if (key == 0 && !this.currentUserDetails && !this.disableCustomerWrite) {
        this.setCustomerDetails({
          firstName: traveller.firstName,
          lastName: traveller.lastName,
          countryCode: traveller.nationality,
        });
      }
    },
    onUpdateOrderDetails(data) {
      this.setCustomerDetails(data);
      this.disableCustomerWrite = true;
    },
    setCustomerDetailsFromUser() {
      if (this.currentUserDetails && !this.disableCustomerWrite) {
        this.setCustomerDetails(
          Object.assign(
            {},
            _.pick(this.currentUserDetails, [
              "firstName",
              "lastName",
              "address",
              "zipCode",
              "city",
              "countryCode",
              "email",
              "phone",
            ])
          )
        );
        this.disableCustomerWrite = true;
      }
    },
    setCustomerDetails(data) {
      this.$set(
        this.bookingForm,
        "customer",
        Object.assign({}, this.bookingForm.customer, data)
      );
    },
    gotoPayment() {
      if (!this.fieldsAreValid) {
        return;
      }

      this.paymentReady = false;
      this.loadingPayment = true;
      this.editMode = false;
    },
    gotoEditMode() {
      if(this.loadingPayment) {
        return;
      }

      this.editMode = true;
    },
    doBooking() {
      if (this.fieldsAreValid) {
        this.$emit("dobooking", this.bookingForm);
      }
    },
  },
  computed: {
    isRqTravel() {
      return this.$store.state.selectedBookingOffer.verifiedOffer.status ===
        "RQ"
        ? true
        : false;
    },
    fieldsAreValid() {
      for (let i in this.isValid) {
        if (typeof this.isValid[i] === "object") {
          for (let y in this.isValid[i]) {
            if (!this.isValid[i][y]) {
              return false;
            }
          }
        } else {
          if (!this.isValid[i]) {
            return false;
          }
        }
      }

      return true;
    },
    travellerNumber() {
      let child = 0;
      let adult = 0;

      return this.bookingForm.travellers.map((traveller) => {
        if (traveller.type != "K") {
          return (adult += 1);
        } else {
          return (child += 1);
        }
      });
    },
    selectedBookingOffer() {
      return this.$store.state.selectedBookingOffer
        ? this.$store.state.selectedBookingOffer
        : null;
    },
    travellerList() {
      return this.selectedBookingOffer
        ? this.selectedBookingOffer.travellers
        : null;
    },
    currentUserDetails() {
      let user = this.$store.state.loggedInUser;
      return !_.isEmpty(user) ? user : null;
    },
    bookingComment() {
      let str = '';
      
      if(this.validOfferCode) {
        str += 'Bruger rabatkoden: ' + this.validOfferCode + "\n\n";
      }

      str += this.userComment;
      return str;
    }
  },
  components: {
    BookingTraveler,
    BookingOrderDetails,
    BookingPaymentDetails,
    BookingPayment,
    MsgBlock,
  },
};
</script>
